import { Component, AfterViewInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
    selector: 'app-detail-panel',
    templateUrl: './detail-panel.component.html',
    styleUrls: ['./detail-panel.component.css']
})
export class DetailPanelComponent implements ICellRendererAngularComp, AfterViewInit, OnDestroy {
    public data: any = {};
    public fromDepositList: boolean = false;
    private params!: ICellRendererParams;
    @ViewChild('root', { static: false }) rootRef!: ElementRef<HTMLElement>;
    private resizeObserver?: ResizeObserver;

    constructor(private permissionService: PermissionService) {}

    canDo(moduleKey: string, actionName: string): boolean {
        return this.permissionService.canDo(moduleKey, actionName);
    }

    agInit(params: ICellRendererParams): void {
        this.params = params;
        // Capturar el parámetro fromDepositList
        this.fromDepositList = (params as any)?.fromDepositList || false;
        // In Master Detail, params.data is the master row data
        const d = params?.data || (params as any)?.node?.data || {};
        this.data = d && d.__isDetail ? d.master : d;
        // Height after initial bind
        setTimeout(() => this.updateRowHeight(), 0);
        setTimeout(() => this.updateRowHeight(), 100);
    }

    refresh(params: ICellRendererParams): boolean {
        this.params = params;
        const d = params?.data || (params as any)?.node?.data || {};
        this.data = d && d.__isDetail ? d.master : d;
        // Height after refresh/bind
        setTimeout(() => this.updateRowHeight(), 0);
        setTimeout(() => this.updateRowHeight(), 100);
        return true;
    }

    // Expansion is managed by the master row (folio) using node.setExpanded()

    get canOpenCartaPorte(): boolean {
        return !!(this.data?.id && this.data?.destination_address && this.data?.download_date);
    }

    get cartaPorteLabel(): string {
        const status = this.data?.carta_porte?.cfdi_status;

        if (status === 'draft') {
            return 'Timbrar carta porte guardada';
        }

        if (status === 'stamped') {
            return 'Ver carta porte';
        }

        return 'Crear carta porte';
    }

    get cartaPorteRoute(): any[] {
        const cartaPorteId = this.data?.carta_porte?.id;
        const status = this.data?.carta_porte?.cfdi_status;

        if (cartaPorteId && status === 'draft') {
            return ['/carta-porte/update', cartaPorteId];
        }

        if (cartaPorteId && status === 'stamped') {
            return ['/carta-porte/view', cartaPorteId];
        }

        return ['/carta-porte/add', this.data?.id];
    }

    get cartaPorteQueryParams(): any {
        return { folio: this.data?.folio };
    }

    // Convert index to alphabet letter: 0 -> a, 1 -> b, ...
    toLetter(index: number): string {
        const base = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        if (index >= 0 && index < base.length) {
            return base[index];
        }
        // Fallback for indexes beyond 'z': repeat last letter
        return base[index % base.length];
    }

    ngAfterViewInit(): void {
        // Defer to ensure the view is fully painted
        setTimeout(() => this.updateRowHeight(), 0);
        // A second pass a bit later to catch async content (images, fonts)
        setTimeout(() => this.updateRowHeight(), 100);
        // Observe size changes to auto-adjust row height
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.updateRowHeight());
            if (this.rootRef?.nativeElement) this.resizeObserver.observe(this.rootRef.nativeElement);
        }
    }

    ngOnDestroy(): void {
        try { this.resizeObserver?.disconnect(); } catch { }
    }

    private updateRowHeight(): void {
        try {
            const api: any = (this.params as any)?.api;
            const rowNode: any = (this.params as any)?.node;
            if (!api || !rowNode) return;
            // Measure the root element height; include small padding
            const el: HTMLElement | null = this.rootRef?.nativeElement || null;
            // Use scrollHeight to capture full content height even if overflowed
            const measured = Math.max(160, Math.min(((el?.scrollHeight || el?.offsetHeight || 0) + 16), 1200));
            if (typeof rowNode.setRowHeight === 'function') {
                rowNode.setRowHeight(measured);
                // Defer the notification slightly to allow height to apply
                setTimeout(() => { try { api.onRowHeightChanged(); } catch { } }, 0);
            }
        } catch { }
    }
}
